import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiEndpoints } from "../../services/apiConfig";

export const createCategory = createAsyncThunk(
  "manageCategory/createCategory",
  async (formDataObj, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const formData = new FormData();
      formData.append("name", formDataObj.name);

      // Append image
      if (formDataObj.image) {
        formData.append("image", formDataObj.image);
      }

      // Append languages with their songs and genres
      formDataObj.languages.forEach((lang, langIndex) => {
        // Append language name
        formData.append(`languages[${langIndex}][name]`, lang.language);

        // Append each song with its genre
        lang.songs.forEach((song, songIndex) => {
          formData.append(
            `languages[${langIndex}][songs][${songIndex}][file]`,
            song.file,
          );
          formData.append(
            `languages[${langIndex}][songs][${songIndex}][genre]`,
            song.genre,
          );
        });
      });

      const response = await axios.post(apiEndpoints.createCategory, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(
        err.response?.data?.detail || err.message || "Category creation failed",
      );
    }
  },
);

export const getAllCategories = createAsyncThunk(
  "orders/getAllCategories",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(apiEndpoints.getCategories, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (error) {
      // Capture API or network errors
      return rejectWithValue(
        error.response?.data || { detail: "Something went wrong" },
      );
    }
  },
);

export const getSongsByCategory = createAsyncThunk(
  "orders/getSongsByCategory",
  async (category, { rejectWithValue }) => {
    try {
      const response = await axios.get(apiEndpoints.getSongsByCategory.replace("{category}", category), {
        headers: {
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (error) {
      // Capture API or network errors
      return rejectWithValue(
        error.response?.data || { detail: "Something went wrong" },
      );
    }
  },
);

export const deleteSong = createAsyncThunk(
  "manageCategory/deleteSong",
  async ({ categoryId, subCategory, songId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const url = apiEndpoints.deleteSong
        .replace("{category_id}", categoryId)
        .replace("{sub_category}", subCategory)
        .replace("{song_id}", songId);

      const response = await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to delete song",
      );
    }
  },
);

export const deleteCategory = createAsyncThunk(
  "manageCategory/deleteCategory",
  async (categoryId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const url = apiEndpoints.deleteCategory.replace(
        "{category_id}",
        categoryId,
      );

      const response = await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to delete category",
      );
    }
  },
);

export const addSongsToCategory = createAsyncThunk(
  "manageCategory/addSongsToCategory",
  async ({ name, language, songs }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const formData = new FormData();
      formData.append("name", name);
      formData.append("language", language);

      // Append each song with its file and genre
      songs.forEach((song, index) => {
        formData.append(`files[${index}][file]`, song.file);
        formData.append(`files[${index}][genre]`, song.genre);
      });

      const response = await axios.post(apiEndpoints.addSongsToCategory, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(
        err.response?.data?.detail || err.message || "Failed to add songs",
      );
    }
  },
);

const manageCategorySlice = createSlice({
  name: "manageCategory",
  initialState: {
    loading: false,
    success: false,
    error: null,
    createCategoryResponse: null,
    deleteSongResponse: null,
    deleteCategoryResponse: null,
    getAllCategoriesResponse: null,
    getSongsByCategoryResponse: null,
    addSongsResponse: null,
  },
  reducers: {
    resetState: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
      state.createCategoryResponse = null;
      state.addSongsResponse = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Category
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.createCategoryResponse = action.payload;
        state.error = null;
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(getAllCategories.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(getAllCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.getAllCategoriesResponse = action.payload;
        state.error = null;
      })
      .addCase(getAllCategories.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(getSongsByCategory.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(getSongsByCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.getSongsByCategoryResponse = action.payload;
        state.error = null;
      })
      .addCase(getSongsByCategory.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(deleteSong.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.deleteSongResponse = action.payload;
        state.error = null;
      })
      .addCase(deleteSong.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.deleteCategoryResponse = action.payload;
        state.error = null;
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(addSongsToCategory.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(addSongsToCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.addSongsResponse = action.payload;
        state.error = null;
      })
      .addCase(addSongsToCategory.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      });
  },
});

// ✅ Export actions and reducer
export const { resetState, clearError } = manageCategorySlice.actions;
export default manageCategorySlice.reducer;
